import { FormLocalizationProvider } from '@faceup/form'
import { defineMessages, LocalizationProvider, useIntl } from '@faceup/localization'
import {
  EnvironmentMessage,
  UiProvider as FUUiProvider,
  type PagesTranslationItems,
  type PagesTranslationVariants,
  type UiProviderProps,
} from '@faceup/ui'
import { type Locale, UiBaseProvider } from '@faceup/ui-base'
import { type ReactNode, useContext, useState } from 'react'
import { LanguageContext } from './Contexts/LanguageContext'
import { SavedReportsProvider } from './Contexts/SavedReportsProvider'
import { useInstitution } from './hooks/useInstitution'
import { useYupLocalization } from './hooks/useYupLocalization'
import { sharedMessages } from './Shared/translations'
import { useTrackPlatform } from './utils/analytics'
import './styles.css'

const messages = defineMessages('followUp.appProviders', [
  { id: 'confirmRedirectMessageTitle', defaultMessage: '' },
  { id: 'confirmRedirectMessageContent', defaultMessage: '' },
  { id: 'redirectOkButton', defaultMessage: '' },
  { id: 'redirectCancelButton', defaultMessage: '' },
  { id: 'ok', defaultMessage: '' },
  { id: 'cancel', defaultMessage: '' },
  {
    id: 'unsavedChangesCount',
    defaultMessage: '',
    values: {
      count: true,
    },
  },
  { id: 'leaveQuestion', defaultMessage: '' },
  { id: 'uploadInputText', defaultMessage: '' },
] as const)

const messagesPages = {
  freeSchool: defineMessages('followUp.appProviders.pages.freeSchool', [
    { id: 'title', defaultMessage: '' },
    { id: 'content', defaultMessage: '' },
  ] as const),
  unregisteredSchool: defineMessages('followUp.appProviders.pages.unregisteredSchool', [
    { id: 'title', defaultMessage: '' },
    { id: 'content', defaultMessage: '' },
  ] as const),
  testingSchool: defineMessages('followUp.appProviders.pages.testingSchool', [
    { id: 'title', defaultMessage: '' },
    { id: 'content', defaultMessage: '' },
  ] as const),
  school: defineMessages('followUp.appProviders.pages.school', [
    { id: 'title', defaultMessage: '' },
    { id: 'content', defaultMessage: '' },
  ] as const),
  company: defineMessages('followUp.appProviders.pages.company', [
    { id: 'title', defaultMessage: '' },
    { id: 'content', defaultMessage: '' },
  ] as const),
  surveyBeforeSend: defineMessages('followUp.appProviders.pages.surveyBeforeSend', [
    { id: 'title', defaultMessage: '' },
    { id: 'content', defaultMessage: '' },
  ] as const),
  surveyAfterSend: defineMessages('followUp.appProviders.pages.surveyAfterSend', [
    { id: 'title', defaultMessage: '' },
    { id: 'content', defaultMessage: '' },
  ] as const),
} satisfies Record<
  Exclude<PagesTranslationVariants, 'formItems'>,
  Record<PagesTranslationItems, unknown>
>

type AppProvidersProps = {
  children: ReactNode
  primaryColor?: string
  direction: 'ltr' | 'rtl'
  antLocale: Locale | undefined
}

const AppProviders = ({ children, primaryColor, direction, antLocale }: AppProvidersProps) => {
  const { formatMessage } = useIntl()
  const institution = useInstitution()
  const { institutionName: name } = institution ?? { name: '' }
  const { language } = useContext(LanguageContext)
  const [isDefaultForm, setIsDefaultForm] = useState(false)

  useTrackPlatform()
  useYupLocalization()

  const translations: NonNullable<UiProviderProps['pages']>['translations'] = {
    company: {
      title: formatMessage(messagesPages.company.title),
      content: formatMessage(messagesPages.company.content)
        .split('{name}')
        .join(name ?? ''),
    },
    school: {
      title: formatMessage(messagesPages.school.title),
      content: formatMessage(messagesPages.school.content)
        .split('{name}')
        .join(name ?? ''),
    },
    testingSchool: {
      title: formatMessage(messagesPages.testingSchool.title),
      content: formatMessage(messagesPages.testingSchool.content)
        .split('{name}')
        .join(name ?? ''),
    },
    freeSchool: {
      title: formatMessage(messagesPages.freeSchool.title),
      content: formatMessage(messagesPages.freeSchool.content)
        .split('{name}')
        .join(name ?? ''),
    },
    unregisteredSchool: {
      title: formatMessage(messagesPages.unregisteredSchool.title),
      content: formatMessage(messagesPages.unregisteredSchool.content)
        .split('{name}')
        .join(name ?? ''),
    },
    surveyBeforeSend: {
      title: formatMessage(messagesPages.surveyBeforeSend.title),
      content: formatMessage(messagesPages.surveyBeforeSend.content),
    },
    surveyAfterSend: {
      title: formatMessage(messagesPages.surveyAfterSend.title),
      content: formatMessage(messagesPages.surveyAfterSend.content),
    },
    formItems: {
      title: '',
      content: '',
    },
  }

  return (
    <LocalizationProvider language={language}>
      <UiBaseProvider colorPrimary={primaryColor} locale={antLocale} direction={direction}>
        <UiProvider
          linkExternalConfirm={{
            confirmRedirectMessageTitle: formatMessage(messages.confirmRedirectMessageTitle),
            confirmRedirectMessageContent: formatMessage(messages.confirmRedirectMessageContent),
            redirectOkButton: formatMessage(messages.redirectOkButton),
            redirectCancelButton: formatMessage(messages.redirectCancelButton),
          }}
          pages={
            institution === null
              ? {
                  mother: { type: null, isTesting: null, plan: null },
                  isDefaultForm,
                  setIsDefaultForm,
                  translations,
                }
              : {
                  mother: institution,
                  isDefaultForm,
                  setIsDefaultForm,
                  translations,
                }
          }
          buttonConfirm={{
            confirm: formatMessage(messages.ok),
            cancel: formatMessage(messages.cancel),
          }}
          attachmentList={{
            download: formatMessage(sharedMessages.download),
          }}
          theme={{
            colors: { primary: primaryColor },
          }}
        >
          <FormLocalizationProvider
            submitButton={{
              send: formatMessage(sharedMessages.send),
              add: formatMessage(sharedMessages.add),
              save: formatMessage(sharedMessages.save),
            }}
            successMessage={{
              send: formatMessage(sharedMessages.savedMessage),
              add: formatMessage(sharedMessages.savedMessage),
              save: formatMessage(sharedMessages.savedMessage),
            }}
            modalForm={{
              cancelButton: formatMessage(sharedMessages.cancel),
            }}
            uploadInput={{
              text: formatMessage(messages.uploadInputText),
            }}
            unsavedChanges={count =>
              formatMessage(messages.unsavedChangesCount, { values: { count } })
            }
            leaveUnsavedChanges={formatMessage(messages.leaveQuestion)}
          >
            <SavedReportsProvider>{children}</SavedReportsProvider>
          </FormLocalizationProvider>
        </UiProvider>
      </UiBaseProvider>
    </LocalizationProvider>
  )
}

const UiProvider = ({ children, ...props }: UiProviderProps) => {
  return (
    <FUUiProvider {...props}>
      {children}
      {['stage', 'preview'].includes(import.meta.env.VITE_ENVIRONMENT ?? '') && (
        <EnvironmentMessage environment={import.meta.env.VITE_ENVIRONMENT ?? ''} />
      )}
    </FUUiProvider>
  )
}

export default AppProviders
